
import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  Code,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid
} from '@chakra-ui/react';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const AdminImplementation = ({ standalone = false }) => {
  const [features, setFeatures] = useState({});
  const [capabilities, setCapabilities] = useState({});
  const [interactions, setInteractions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const Wrapper = React.Fragment;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch features
        const featuresResponse = await fetchWithAuth('/api/core-features-registry/features');
        const featuresData = await featuresResponse.json();
        
        // Fetch capabilities
        const capabilitiesResponse = await fetchWithAuth('/api/core-features-registry/capabilities');
        const capabilitiesData = await capabilitiesResponse.json();
        
        // Fetch component interactions
        const interactionsResponse = await fetchWithAuth('/api/core-features-registry/component-interactions');
        const interactionsData = await interactionsResponse.json();
        
        setFeatures(featuresData.features || {});
        setCapabilities(capabilitiesData.capabilities || {});
        setInteractions(interactionsData.interactions || {});
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return (
    <Wrapper>
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Spinner size="xl" />
      </Box>
    </Wrapper>
  );

  if (error) return (
    <Wrapper>
      <Alert status="error">
        <AlertIcon />
        Error loading implementation data: {error}
      </Alert>
    </Wrapper>
  );

  return (
    <Wrapper>
      <Box width="100%" mx="auto" bg="white" p={6}>
        <VStack align="flex-start" spacing={6} width="100%">
          <Box>
            <Heading size="xl" mb={2}>Implementation Overview</Heading>
            <Text color="gray.600">
              How capabilities are implemented across components and their interactions
            </Text>
          </Box>

          <Tabs width="100%" colorScheme="blue">
            <TabList>
              <Tab>Capability Implementation</Tab>
              <Tab>Component Mapping</Tab>
              <Tab>Component Interactions</Tab>
              <Tab>Implementation Stats</Tab>
            </TabList>

            <TabPanels>
              {/* Capability Implementation Tab */}
              <TabPanel>
                <VStack align="start" spacing={4} width="100%">
                  <Heading size="lg">Capabilities by Implementation</Heading>
                  <Table variant="striped" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Capability</Th>
                        <Th>Implemented In</Th>
                        <Th>Components</Th>
                        <Th>Total Features</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Object.entries(capabilities).map(([capability, implementations]) => (
                        <Tr key={capability}>
                          <Td>
                            <Code fontSize="sm">{capability}</Code>
                          </Td>
                          <Td>
                            <VStack align="start" spacing={1}>
                              {implementations.map((impl, index) => (
                                <Text key={index} fontSize="sm">
                                  {impl.feature_name}
                                </Text>
                              ))}
                            </VStack>
                          </Td>
                          <Td>
                            <VStack align="start" spacing={1}>
                              {implementations.map((impl, index) => (
                                <Code key={index} fontSize="xs">
                                  {impl.component}
                                </Code>
                              ))}
                            </VStack>
                          </Td>
                          <Td>
                            <Badge colorScheme="blue">{implementations.length}</Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </VStack>
              </TabPanel>

              {/* Component Mapping Tab */}
              <TabPanel>
                <VStack align="start" spacing={4} width="100%">
                  <Heading size="lg">Component to Feature Mapping</Heading>
                  <Table variant="striped" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Component</Th>
                        <Th>Feature</Th>
                        <Th>Path</Th>
                        <Th>Capabilities</Th>
                        <Th>AI Enabled</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Object.entries(features).map(([featureId, feature]) => (
                        <Tr key={featureId}>
                          <Td>
                            <Code fontSize="sm">{feature.component}</Code>
                          </Td>
                          <Td>
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="semibold">{feature.name}</Text>
                              <Code fontSize="xs">{featureId}</Code>
                            </VStack>
                          </Td>
                          <Td>
                            <Code fontSize="xs">{feature.component_path}</Code>
                          </Td>
                          <Td>
                            <Badge colorScheme="green">
                              {feature.capabilities?.length || 0}
                            </Badge>
                          </Td>
                          <Td>
                            {feature.ai_capabilities ? (
                              <Badge colorScheme="blue">
                                {Object.keys(feature.ai_capabilities).length} AI caps
                              </Badge>
                            ) : (
                              <Badge colorScheme="gray">No AI</Badge>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </VStack>
              </TabPanel>

              {/* Component Interactions Tab */}
              <TabPanel>
                <VStack align="start" spacing={4} width="100%">
                  <Heading size="lg">Component Interactions</Heading>
                  <Table variant="striped" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Interaction</Th>
                        <Th>Source Component</Th>
                        <Th>Target Component</Th>
                        <Th>Type</Th>
                        <Th>Context Shared</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Object.entries(interactions).map(([interactionId, interaction]) => (
                        <Tr key={interactionId}>
                          <Td>
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="semibold">{interactionId}</Text>
                              <Text fontSize="xs" color="gray.600">
                                {interaction.description}
                              </Text>
                            </VStack>
                          </Td>
                          <Td>
                            <Code fontSize="sm">{interaction.source}</Code>
                          </Td>
                          <Td>
                            <Code fontSize="sm">{interaction.target}</Code>
                          </Td>
                          <Td>
                            <Badge colorScheme="purple">
                              {interaction.interaction_type}
                            </Badge>
                          </Td>
                          <Td>
                            <VStack align="start" spacing={1}>
                              {interaction.context_shared?.map((context, index) => (
                                <Badge key={index} size="sm" variant="outline">
                                  {context}
                                </Badge>
                              ))}
                            </VStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </VStack>
              </TabPanel>

              {/* Implementation Stats Tab */}
              <TabPanel>
                <VStack align="start" spacing={6} width="100%">
                  <Heading size="lg">Implementation Statistics</Heading>
                  
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} width="100%">
                    <Card>
                      <CardBody>
                        <Text fontSize="2xl" fontWeight="bold">{Object.keys(features).length}</Text>
                        <Text fontSize="sm" color="gray.600">Total Features</Text>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Text fontSize="2xl" fontWeight="bold">{Object.keys(capabilities).length}</Text>
                        <Text fontSize="sm" color="gray.600">Unique Capabilities</Text>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Text fontSize="2xl" fontWeight="bold">
                          {Object.values(features).filter(f => f.ai_capabilities).length}
                        </Text>
                        <Text fontSize="sm" color="gray.600">AI-Enabled Features</Text>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Text fontSize="2xl" fontWeight="bold">{Object.keys(interactions).length}</Text>
                        <Text fontSize="sm" color="gray.600">Component Interactions</Text>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Text fontSize="2xl" fontWeight="bold">
                          {Object.values(features).reduce((acc, f) => 
                            acc + (f.ai_capabilities ? Object.keys(f.ai_capabilities).length : 0), 0)}
                        </Text>
                        <Text fontSize="sm" color="gray.600">Total AI Capabilities</Text>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  {/* Implementation Details */}
                  <Box width="100%">
                    <Heading size="md" mb={4}>Implementation Architecture</Heading>
                    <VStack align="start" spacing={4}>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Core Architecture Components:</Text>
                        <VStack align="start" spacing={2}>
                          <HStack>
                            <Code>ComponentActionHandler.js</Code>
                            <Badge colorScheme="blue">Core capability execution engine</Badge>
                          </HStack>
                          <HStack>
                            <Code>AIComponentInteractionManager.js</Code>
                            <Badge colorScheme="green">AI component registration & management</Badge>
                          </HStack>
                          <HStack>
                            <Code>core-features-registry.js</Code>
                            <Badge colorScheme="purple">Feature & capability definitions</Badge>
                          </HStack>
                          <HStack>
                            <Code>agents.js</Code>
                            <Badge colorScheme="orange">AI agent routing & mission management</Badge>
                          </HStack>
                        </VStack>
                      </Box>

                      <Box>
                        <Text fontWeight="bold" mb={2}>Key Implementation Patterns:</Text>
                        <VStack align="start" spacing={2}>
                          <Text fontSize="sm">• Capabilities defined in registry, implemented in ComponentActionHandler</Text>
                          <Text fontSize="sm">• AI agents route to missions based on capability requirements</Text>
                          <Text fontSize="sm">• Components register capabilities with AIComponentInteractionManager</Text>
                          <Text fontSize="sm">• Real-time capability enable/disable through registry API</Text>
                          <Text fontSize="sm">• Dynamic agent capability validation through getCapabilityContext()</Text>
                          <Text fontSize="sm">• Agent architecture context through getArchitectureContext()</Text>
                        </VStack>
                      </Box>

                      <Box>
                        <Text fontWeight="bold" mb={2}>Agent Integration Architecture:</Text>
                        <VStack align="start" spacing={2}>
                          <Text fontSize="sm">• Agents have primary_ai_capabilities array linked to registry</Text>
                          <Text fontSize="sm">• Dynamic capability instruction generation via generateCapabilityInstructions()</Text>
                          <Text fontSize="sm">• Mission routing based on capability requirements</Text>
                          <Text fontSize="sm">• Real-time capability validation with canAgentExecuteCapability()</Text>
                          <Text fontSize="sm">• Multi-layer validation: Registry → Implementation → Agent Config</Text>
                        </VStack>
                      </Box>
                    </VStack>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Box>
    </Wrapper>
  );
};

export default AdminImplementation;
